import { UnleashClient } from '@unleash/proxy-client-vue'
import type {
  IConfig,
  IMutableContext,
  IVariant,
  UnleashClient as UnleashClientType,
} from '@unleash/proxy-client-vue'
import type { User } from '@/types'
import type { App } from 'vue'
import type { Environment } from '@/config'
import { throttle } from 'throttle-debounce'

let client: UnleashClientType | null = null

// Since we are going to be throttling the context updates, we need to store the pending updates.
let pendingContextUpdate: IMutableContext = {}

export default function createUnleashClient(
  environment: Environment,
): UnleashClientType {
  const unleashKey = process.env.VUE_APP_UNLEASH_KEY
  const hasUnleash = unleashKey !== undefined && unleashKey !== ''
  const url = hasUnleash
    ? 'https://app.unleash-hosted.com/sunshine/api/frontend'
    : 'https://unleash.storyblok.com/proxy'
  const clientKey = hasUnleash && unleashKey ? unleashKey : 'unleash'
  const config: IConfig = {
    url,
    clientKey,
    appName: 'default',
    environment,
    context: {
      properties: {
        hostname: window.location.hostname,
      },
    },
  }

  client = new UnleashClient(config)

  return client
}

const throttledUpdateContext = throttle(
  200,
  (): void => {
    if (client && Object.keys(pendingContextUpdate).length > 0) {
      // The lovely client.updateContext method does not merge the context, it overwrites it.
      const existingContext = client.getContext()
      const updatedContext = {
        ...existingContext,
        ...pendingContextUpdate,
        properties: {
          ...existingContext.properties,
          ...pendingContextUpdate.properties,
        },
      }
      client.updateContext(updatedContext)
      pendingContextUpdate = {} // Reset after applying
    }
  },
  {
    noTrailing: false,
    noLeading: true,
  },
)

export const connectUnleashUser = (user: User): void => {
  if (!client) {
    return
  }

  if (user?.id) {
    pendingContextUpdate.userId = String(user.id)
  }

  if (user?.email) {
    if (!pendingContextUpdate.properties) {
      pendingContextUpdate.properties = {}
    }

    const emailDomain = user.email.split('@')[1]
    if (emailDomain) {
      pendingContextUpdate.properties.userDomain = String(emailDomain)
    }
    pendingContextUpdate.properties.userMail = String(user.email)
  }

  if (user?.has_org) {
    if (!pendingContextUpdate.properties) {
      pendingContextUpdate.properties = {}
    }
    pendingContextUpdate.properties.orgName = String(user.org?.name)
  }

  throttledUpdateContext()
}

export const toggleUnleashPartner = (partnerId: number): void => {
  if (!client) {
    return
  }

  if (partnerId) {
    if (!pendingContextUpdate.properties) {
      pendingContextUpdate.properties = {}
    }
    pendingContextUpdate.properties.partnerId = String(partnerId)
    throttledUpdateContext()
  }
}

export const toggleUnleashSpace = (spaceId: number): void => {
  if (!client) {
    return
  }

  if (spaceId) {
    if (!pendingContextUpdate.properties) {
      pendingContextUpdate.properties = {}
    }
    pendingContextUpdate.properties.spaceId = String(spaceId)
    throttledUpdateContext()
  }
}

export const unleashFlagPlugin = {
  install: function (app: App): void {
    app.config.globalProperties.$useFlag = (value: string): boolean =>
      client?.isEnabled(value) || false
  },
}

export const unleashBeVersion = (): string | null => {
  if (!client) {
    return null
  }

  return client.isEnabled('storyrails-canary') ? 'canary' : null
}

/**
 * We need to ensure the unleash client has been installed and in its ready state.
 * Before retrieving it's variant so as to not get invalid variant values.
 *
 * This function should be used in places in places where the client is not guaranteed to be ready. e.g vue stores (useTracking).
 * Otherwise make use of the `useVariant` composable instead.
 *
 * @param {string} flagName - The name of the feature flag
 * @return {IVariant | null} - The variant of the feature flag or null if the client is not ready
 */
export const getVariant = async (
  flagName: string,
): Promise<IVariant | null> => {
  return new Promise((resolve) => {
    if (!client) {
      return resolve(null)
    }

    if (client.isReady()) {
      return resolve(client.getVariant(flagName))
    }

    let resolved = false
    // Allow 3 seconds for the client to become ready and provide a feature flag variant.
    // If it is not ready within this time, default to resolving with null.
    const unleashTimeout = setTimeout(() => {
      resolved = true
      resolve(null)
    }, 3000)

    client.on('ready', () => {
      if (!resolved) {
        clearTimeout(unleashTimeout)
        return resolve(client?.getVariant(flagName) ?? null)
      }
    })
  })
}
